/**
 * src/components/admin/AdminLoginForm.tsx
 *
 * File-level:
 * - Small, reusable admin login form component.
 * - Handles local form state, validation and calls loginAdmin API helper.
 */

import React, { useState } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { loginAdmin, AdminUser } from '../../services/adminApi';

 /**
  * Props for AdminLoginForm
  *
  * @interface AdminLoginFormProps
  * @property onSuccess Called with AdminUser when login succeeds
  */
 export interface AdminLoginFormProps {
   onSuccess: (user: AdminUser) => void;
 }

 /**
  * AdminLoginForm
  *
  * @description Reusable login form component that authenticates admin users.
  */
 export default function AdminLoginForm({ onSuccess }: AdminLoginFormProps): JSX.Element {
   const [username, setUsername] = useState('');
   const [password, setPassword] = useState('');
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState('');
   const [showPassword, setShowPassword] = useState(false);

   /**
    * handleSubmit
    *
    * @description Validate input and call loginAdmin API helper.
    * @param e React.FormEvent
    */
   async function handleSubmit(e: React.FormEvent) {
     e.preventDefault();
     setError('');
     setLoading(true);

     if (!username || username.trim().length < 1) {
       setError('Please enter username');
       setLoading(false);
       return;
     }
     if (!password || password.length < 4) {
       setError('Please enter a valid password');
       setLoading(false);
       return;
     }

     try {
       const resp = await loginAdmin({ userId: username.trim(), password });
       if (!resp.success) {
         setError(resp.message || 'Login failed');
         setLoading(false);
         return;
       }

       if (resp.user) {
         // Notify parent page
         onSuccess(resp.user);
       } else {
         setError('Login succeeded but no user data returned');
       }
     } catch (err: any) {
       setError('Unexpected error during login');
       // eslint-disable-next-line no-console
       console.error('AdminLoginForm error', err);
     } finally {
       setLoading(false);
     }
   }

   return (
     <form onSubmit={handleSubmit} className="space-y-4" aria-label="Admin login form">
       <div>
         <Label htmlFor="admin-username" className="text-sm font-medium">
           Username
         </Label>
         <Input
           id="admin-username"
           type="text"
           placeholder="admin user id"
           value={username}
           onChange={(e) => setUsername(e.target.value)}
           disabled={loading}
           required
           className="border-gray-300"
         />
       </div>

       <div>
         <Label htmlFor="admin-password" className="text-sm font-medium">
           Password
         </Label>
         <div className="relative">
           <Input
             id="admin-password"
             type={showPassword ? 'text' : 'password'}
             placeholder="password"
             value={password}
             onChange={(e) => setPassword(e.target.value)}
             disabled={loading}
             required
             className="border-gray-300 pr-10"
           />
           <Button
             type="button"
             variant="ghost"
             size="sm"
             className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
             onClick={() => setShowPassword(!showPassword)}
             disabled={loading}
           >
             {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
           </Button>
         </div>
       </div>

       {error && (
         <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
           <p className="text-red-700 text-sm">{error}</p>
         </div>
       )}

       <Button
         type="submit"
         className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
         disabled={loading}
       >
         {loading ? (
           <>
             <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 inline-block" />
             Signing in...
           </>
         ) : (
           <>
             <LogIn className="w-4 h-4 mr-2 inline" />
             Sign in as Admin
           </>
         )}
       </Button>
     </form>
   );
 }